import {
  type FAQ,
  getAllFaqCategories,
  getAllFaqSlugs,
  getFaqBySlug,
} from '@bulknutrients/bulk-shared-lib/lib/sanity/faq';
import { getRedirect } from '@bulknutrients/bulk-shared-lib/lib/sanity/redirect';
import { Breadcrumbs, type Crumb } from '@components/Breadcrumbs/Breadcrumbs';
import { Container } from '@components/Container';
import { StandardLayout } from '@components/Layouts';
import { PortableTextBlockRenderer } from '@components/PortableText/PortableText';
import { TextLink } from '@components/Typography/TextLink/TextLink';
import type { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'next/head';
import type { ReactElement } from 'react';

export default function QuestionPage({
  questionData,
  breadcrumbs,
}: {
  questionData: FAQ;
  breadcrumbs: Crumb[];
}): ReactElement {
  return (
    <>
      <Head>
        <title>{questionData.metaTitle}</title>
        <meta name="title" content={questionData.metaTitle} />
        <meta name="description" content={questionData.metaDescription} />
        <link
          rel="canonical"
          href={`${process.env.NEXT_PUBLIC_SERVER_URL}${questionData.slug}`}
        />
      </Head>
      <Container>
        <Breadcrumbs crumbs={breadcrumbs} />
      </Container>
      <Container
        as="main"
        id="main-content"
        className="mb-12.5 grid-cols-[1fr_min(var(--width-narrow-page),calc(100%-16px))_1fr]"
      >
        <h1>{questionData.title}</h1>
        <div>
          <PortableTextBlockRenderer content={questionData.body} />
          {questionData.relatedFaqs?.length && (
            <>
              <h2 className="italic">Related FAQs</h2>
              <ul className="list-disc pl-4">
                {questionData.relatedFaqs.map((q) => (
                  <li key={q.title} className="my-3">
                    <TextLink href={q.slug}>{q.title}</TextLink>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </Container>
    </>
  );
}

QuestionPage.getLayout = function getLayout(page: ReactElement) {
  return <StandardLayout>{page}</StandardLayout>;
};

export const getStaticPaths: GetStaticPaths = async () => {
  const faqSlugs = await getAllFaqSlugs();
  return {
    paths: faqSlugs,
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const category = params?.category?.toString();
  const question = params?.question?.toString();

  if (!category || !question) {
    return {
      notFound: true,
    };
  }

  const redirect = await getRedirect(
    `/faqs/${category}/${question}`.split(',').join('/') as string
  );

  if (redirect) {
    return {
      redirect: {
        destination: redirect.newUrl,
        permanent: redirect.permanent,
      },
    };
  }

  // faq category slug is formatted and won't match anything in sanity,
  // need to get title and slug from getFaqCategoryNames and match
  const categories = await getAllFaqCategories();
  const categoryInfo = categories.filter((c) => c.slug.includes(category))[0];
  const questionData = await getFaqBySlug(`/faqs/${category}/${question}`);

  if (!questionData || !categoryInfo) {
    return {
      notFound: true,
    };
  }

  const breadcrumbs = [
    { title: 'home', path: '/' },
    { title: 'FAQs', path: '/faqs' },
    { title: categoryInfo.title, path: categoryInfo.slug },
  ];

  return {
    props: {
      questionData,
      breadcrumbs,
    },
  };
};
